
/// <amd-module name="Environments/Providers/VirtualMachineGroups/ActionsCreators/VirtualMachineGroupActionCreator" />
import { ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { IVssPageContext } from "VSS/Platform/Context";
import { PageKey } from "Environments/Providers/VirtualMachineGroups/Utilities"; 
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { getErrorMessage } from "Environments/EnvironmentsCommon/Utils";
import { VirtualMachineGroup, PackageMetadata, TaskAgentPool, VirtualMachine } from "DistributedTask/Client/Contracts/TaskAgent";
import { VirtualMachineGroupActions } from "Environments/Providers/VirtualMachineGroups/Actions/VirtualMachineGroupActions";
import { IVirtualMachineGroupService } from "Environments/Providers/VirtualMachineGroups/Services/VirtualMachineGroupServices";
import { IVirtualMachineGroupTargetsResult } from "Environments/Providers/VirtualMachineGroups/Services/VirtualMachineGroupExtendedClient";
import { IVirtualMachineTagsInfo } from "Environments/Providers/VirtualMachineGroups/Common/Utils";

export class VirtualMachineGroupActionCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "virtualmachines-service-group-actions-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);

        this._actions = ActionsHubManager.CreateActionsHub<VirtualMachineGroupActions>(VirtualMachineGroupActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions> (MessagesActions, instanceId);
    }

    public getVirtualMachineGroup(pageContext: IVssPageContext, environmentId: number, virtualMachineGroupId: number) {
        let virtualMachineGroupPromise = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService").getVirtualMachineGroupById(environmentId, virtualMachineGroupId);
        virtualMachineGroupPromise.then((virtualMachineGroup: VirtualMachineGroup) => {
            this._actions.setVirtualMachineGroup.invoke(virtualMachineGroup);
        }, (error: any) => {
            this._messageActions.addMessage.invoke({ key: PageKey.VirtualMachineGroupTargetsView, value: getErrorMessage(error) });
        });
    }

    public getVirtualMachines(pageContext: IVssPageContext, environmentId: number, virtualMachineGroupId: number, continuationToken?: string, top?: number) {
        let virtualMachineGroupTargetsResultPromise = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService").getVirtualMachines(environmentId, virtualMachineGroupId, continuationToken, top);
        virtualMachineGroupTargetsResultPromise.then((virtualMachineGroupTargetResult: IVirtualMachineGroupTargetsResult) => {
            this._actions.setVirtualMachineGroupTargetsResult.invoke(virtualMachineGroupTargetResult);
        }, (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.VirtualMachineGroupTargetsView, value: getErrorMessage(error) });
        });
        
    }

    public loadMoreVirtualMachines(pageContext: IVssPageContext, environmentId: number, virtualMachineGroupId: number, continuationToken?: string, top?: number) {
        top = (!top || top > 25) ? top : 25; 
        let virtualMachineGroupTargetsResultPromise = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService").getVirtualMachines(environmentId, virtualMachineGroupId, continuationToken, top);
        virtualMachineGroupTargetsResultPromise.then((virtualMachineGroupTargetResult: IVirtualMachineGroupTargetsResult) => {
            this._actions.loadMoreVirtualMachineGroupTargetsResult.invoke(virtualMachineGroupTargetResult);
        }, (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.VirtualMachineGroupTargetsView, value: getErrorMessage(error) });
        });
        
    }

    public getTargetPackages(pageContext: IVssPageContext) {
        let virtualMachineGroupService = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService");
        virtualMachineGroupService.getTargetPackages().then((packages: PackageMetadata[]) => {
            this._actions.setTargetPackages.invoke(packages);
        }, (error: any) => {
            this._messageActions.addMessage.invoke({ key: PageKey.AddTargetView, value: getErrorMessage(error) });
        });
    }

    public getTaskAgentPool(pageContext: IVssPageContext, poolId: number) {
        let virtualMachineGroupService = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService");
        virtualMachineGroupService.getTaskAgentPool(poolId).then((taskAgentPool: TaskAgentPool) => {
            this._actions.setTaskAgentPool.invoke(taskAgentPool);
        }, (error: any) => {
            this._messageActions.addMessage.invoke({ key: PageKey.AddTargetView, value: getErrorMessage(error) });
        });
    }

    public getPATForDeploymentPool(pageContext: IVssPageContext, poolId: number) {
        let virtualMachineGroupService = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService");
        virtualMachineGroupService.getPATForDeploymentPool(poolId).then((patToken: string) => {
            this._actions.setPATForDeploymentPool.invoke(patToken);
        }, (error: any) => {
            this._messageActions.addMessage.invoke({ key: PageKey.AddTargetView, value: getErrorMessage(error) });
        });
    }

    public virtualMachineTagUpdated(machine: VirtualMachine, tags: string[]) {
        this._actions.onVirtualMachineTagUpdated.invoke({ machine: machine, tags: tags} as IVirtualMachineTagsInfo);
    }

    public updateVirtualMachines(pageContext: IVssPageContext, environmentId: number, virtualMachineGroupId: number, virtualMachines: VirtualMachine[]) {
        let virtualMachineGroupService = pageContext.getService<IVirtualMachineGroupService>("IVirtualMachineGroupService");
        virtualMachineGroupService.updateVirtualMachines(environmentId, virtualMachineGroupId, virtualMachines).then((virtualMachines: VirtualMachine[]) => {
            this._actions.onVirtualMachinesUpdated.invoke(virtualMachines);
        }, (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.VirtualMachineGroupTargetsView, value: getErrorMessage(error) });
        });
    }

    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<VirtualMachineGroupActions>(VirtualMachineGroupActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _actions: VirtualMachineGroupActions;
    private _messageActions: MessagesActions;
}
